import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiCheckCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";
import { object, string } from "yup";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import { routes } from "constants/routes";
import { RESPONSE_STATUS } from "constants/status";
import { showErrorMessage } from "helpers/toastMessage";
import AuthLayout from "layouts/Auth";
import { resetPasswordAPI } from "services/api/user";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

interface IResetPasswordForm {
  email: string;
}

const resetPasswordFormSchema = object({
  email: string().required("This field is required").email("Email is not a valid email")
});

export default function ResetPassword() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { trackAllEvents } = useAnalytics();
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();
  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit
  } = useForm<IResetPasswordForm>({ resolver: yupResolver(resetPasswordFormSchema) });

  const onSubmit = async ({ email }: IResetPasswordForm) => {
    dispatch(handleLoading(true));

    const {
      data: { status, message }
    } = await resetPasswordAPI(email).finally(() => dispatch(handleLoading(false)));

    if (status === RESPONSE_STATUS.SUCCESS) {
      toggleSuccessModal();
      trackAllEvents("auth_signin_reset_password");
    } else {
      showErrorMessage(message);
    }
  };

  const goToSignIn = () => {
    navigate(routes.SIGNIN);
  };

  return (
    <AuthLayout>
      <form className={styles.resetPasswordContainer} onSubmit={handleSubmit(onSubmit)}>
        <h1 className="text-xl desktop:text-2xl">Reset Password</h1>
        <div className={styles.body}>
          <span>We’ll email you a link to reset our password</span>

          <Input
            name="email"
            type="email"
            register={register}
            error={errors.email}
            label="&nbsp;Email Address"
            labelClass="desktop:!text-base"
          />
        </div>

        <button
          className="self-start -my-3 text-sm underline desktop:-my-12 text-primary"
          onClick={goToSignIn}>
          Go Back To Sign In
        </button>

        <Button
          type="submit"
          size="lg"
          className="!px-8 desktop:!px-12 desktop:text-lg w-fit"
          loading={isSubmitting}>
          Reset Password
        </Button>
      </form>

      <Modal
        isOpen={isSuccessModalOpen}
        closeModal={toggleSuccessModal}
        wrapperClass="!m-auto text-sm desktop:!min-h-fit desktop:!min-w-fit"
        dialogClass="!m-auto !bg-foreground !p-4 !border !border-card !overflow-y-auto gap-4"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <div className="flex gap-1 text-xl font-bold text-success">
          <Icon path={mdiCheckCircleOutline} />
          <span>Success</span>
        </div>
        <p className="px-6">We sent you an email with the password reset link. Thank you.</p>
        <Button onClick={goToSignIn} className="self-end w-20 rounded-full h-9">
          OK
        </Button>
      </Modal>
    </AuthLayout>
  );
}
